import  { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MdDelete } from "react-icons/md";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUserAuth } from "../../context/UserAuthContext";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./productMgmt.css";

const EditProduct = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, token, currency } = useUserAuth();
  const merchant_id = user.uid;


  const [loading, setLoading] = useState(false);
  const [merchant, setMerchant] = useState({});
  const [product, setProduct] = useState({
    sub_cat_id: "",
    name: "",
    price: "",
    discount_price: "",
    product_description: "",
  });
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [errors, setErrors] = useState({});

 
  useEffect(() => {
    if (!merchant_id) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productRes, merchantRes] = await Promise.all([
          axios.get(`${base_url}/api/v1/products/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${base_url}/api/v1/users/id/${merchant_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const data = productRes?.data?.data;
        setProduct({
          ...data,
         
          price: data.price ? Math.floor(data.price * currency.value) : "",
          discount_price: data.discount_price
            ? Math.floor(data.discount_price * currency.value)
            : "",
        });

        setExistingImages(productRes?.data?.data?.images || []);
        setMerchant(merchantRes?.data);
      } catch (error) {
        toast.error("Failed to load product data");
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, merchant_id, base_url, token, currency]);

  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProduct((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };


  const handleDescriptionChange = (content) => {
    setProduct((prev) => ({ ...prev, product_description: content }));
    if (errors.description) {
      setErrors((prev) => ({ ...prev, description: "" }));
    }
  };

  
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + existingImages.length + newImages.length > 10) {
      toast.error("You can upload maximum 10 images in total");
      return;
    }
    setNewImages((prev) => [...prev, ...files]);
    if (errors.images) {
      setErrors((prev) => ({ ...prev, images: "" }));
    }
  };

 
  const deleteExistingImage = async (imageId) => {
    try {
      await axios.delete(`${base_url}/api/v1/products/image/${imageId}`, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      setExistingImages((prev) => prev.filter((img) => img.image_id !== imageId));
      toast.success("Image deleted successfully");
    } catch (err) {
      toast.error("Failed to delete image");
      console.error(err);
    }
  };

  
  const deleteNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

 
  const validateForm = () => {
    const newErrors = {};
    if (!product.name.trim()) newErrors.name = "Product name is required";
    if (!product.product_description.trim())
      newErrors.description = "Description is required";
    if (existingImages.length === 0 && newImages.length === 0) {
      newErrors.images = "At least one image is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      const formData = new FormData();

     
      formData.append("name", product.name);
      formData.append("product_description", product.product_description);
      formData.append("merchant_id", merchant_id);
      formData.append("stock", true);
      formData.append("cat_id", merchant.category_id);
      formData.append("sub_cat_id", product.sub_cat_id || "");

      
      if (product.price !== "" && product.price != null) {
        const formatPrice = Math.floor(
          Number(product.price) * currency.country_value
        );
        formData.append("price", formatPrice);
      }


      if (product.discount_price !== "" && product.discount_price != null) {
        const formatDiscountPrice = Math.floor(
          Number(product.discount_price) * currency.country_value
        );
        formData.append("discount_price", formatDiscountPrice);
      }

     
      newImages.forEach((file) => formData.append("image", file));

      const response = await axios.put(
        `${base_url}/api/v1/products/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Product updated successfully");
        navigate("/dashboard/product-list");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update product");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  
  const discountPercent =
    product.price && product.discount_price
      ? (((product.price - product.discount_price) / product.price) * 100).toFixed(1)
      : 0;

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <Link to="/dashboard/product-list" className="btn btn-primary">
                  Back to Products
                </Link>
                <h5 className="mb-0">Update Product</h5>
              </div>

              <div className="card-body">
                {loading && (
                  <div className="overlay">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="product-form">
                 
                  <div className="mb-3">
                    <label htmlFor="productName" className="form-label">
                      Product Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? "is-invalid" : ""}`}
                      id="productName"
                      name="name"
                      value={product.name}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                    {errors.name && (
                      <div className="invalid-feedback">{errors.name}</div>
                    )}
                  </div>

                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="price" className="form-label">
                        Price
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">{currency?.symbol}</span>
                        <input
                          type="number"
                          className={`form-control ${errors.price ? "is-invalid" : ""}`}
                          id="price"
                          name="price"
                          value={product.price}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          disabled={loading}
                        />
                      </div>
                      {errors.price && (
                        <div className="invalid-feedback">{errors.price}</div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="discountPrice" className="form-label">
                        Discount Price
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">{currency?.symbol}</span>
                        <input
                          type="number"
                          className={`form-control ${errors.discount_price ? "is-invalid" : ""}`}
                          id="discountPrice"
                          name="discount_price"
                          value={product.discount_price}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          disabled={loading}
                        />
                      </div>
                      {errors.discount_price && (
                        <div className="invalid-feedback">{errors.discount_price}</div>
                      )}
                    </div>
                  </div>

                  
                  {product.discount_price && (
                    <div className="mb-3 p-3 bg-light rounded">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <span className="fw-bold">
                            {currency?.symbol} {product.discount_price}
                          </span>
                          <span className="text-muted ms-2">
                            <del>
                              {currency?.symbol} {product.price}
                            </del>
                          </span>
                        </div>
                        <div>
                          <span className="badge bg-success">{discountPercent}% OFF</span>
                        </div>
                      </div>
                    </div>
                  )}

                 
                  <div className="mb-3">
                    <label className="form-label">
                      Product Description <span className="text-danger">*</span>
                    </label>
                    <ReactQuill
                      theme="snow"
                      value={product.product_description}
                      onChange={handleDescriptionChange}
                      placeholder="Describe your product in detail..."
                      className={`editor-input ${errors.description ? "border-danger" : ""}`}
                      readOnly={loading}
                    />
                    {errors.description && (
                      <div className="text-danger small mt-1">{errors.description}</div>
                    )}
                  </div>

                  
                  <div className="mb-4">
                    <label className="form-label">
                      Product Images <span className="text-danger">*</span>
                    </label>
                    <input
                      type="file"
                      className={`form-control ${errors.images ? "is-invalid" : ""}`}
                      onChange={handleImageUpload}
                      accept="image/*"
                      multiple
                      disabled={loading}
                    />
                    <div className="form-text">
                      Upload up to 10 images total (first image will be used as main)
                    </div>
                    {errors.images && <div className="invalid-feedback">{errors.images}</div>}

                   
                    <div className="row mt-3 g-2">
                      {existingImages.length > 0 &&
                        existingImages.map((img, index) => (
                          <div
                            className="col-6 col-sm-4 col-md-3 col-lg-2"
                            key={`existing-${index}`}
                          >
                            <div className="image-preview-container">
                              <img
                                src={img.desktop_image}
                                alt={`Product ${index}`}
                                className="img-thumbnail"
                              />
                              <button
                                type="button"
                                className="btn btn-danger btn-sm delete-btn"
                                onClick={() => deleteExistingImage(img.image_id)}
                                disabled={loading}
                              >
                                <MdDelete size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>

                   
                    <div className="row mt-3 g-2">
                      {newImages.length > 0 &&
                        newImages.map((file, index) => (
                          <div
                            className="col-6 col-sm-4 col-md-3 col-lg-2"
                            key={`new-${index}`}
                          >
                            <div className="image-preview-container">
                              <img
                                src={URL.createObjectURL(file)}
                                alt={`New image ${index}`}
                                className="img-thumbnail"
                              />
                              <button
                                type="button"
                                className="btn btn-danger btn-sm delete-btn"
                                onClick={() => deleteNewImage(index)}
                                disabled={loading}
                              >
                                <MdDelete size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>

                    {existingImages.length === 0 && newImages.length === 0 && (
                      <div className="col-12">
                        <div className="text-center py-4 border rounded bg-light">
                          <p className="text-muted mb-0">No images selected</p>
                        </div>
                      </div>
                    )}
                  </div>

                 
                  <div className="d-grid gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Updating...
                        </>
                      ) : (
                        "Update Product"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProduct;